/**
 * New Size Requests — admin view.
 *
 * A coach found a jersey was the wrong size. The office posts a replacement
 * and the coach confirms the player received it, so the full journey is
 * visible here: Requested, Sent Out, Received.
 *
 * The player's registered size is never changed by any of this. The column
 * headed Original is what was ordered at registration; Requested is what
 * replaced it, so the two can always be compared.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Ruler, Send, X } from "lucide-react";
import toast from "react-hot-toast";

import DataTable from "../../components/admin/DataTable";
import FilterBar from "../../components/admin/FilterBar";
import Header from "../../components/Header";
import jerseyService from "../../api/services/jersey.service";
import { formatDate } from "../../utils/format";

/** "youth_m" reads as "Youth M". */
const formatSize = (size) =>
  size
    ? size
        .split("_")
        .map((part) =>
          part === "youth" || part === "adult"
            ? part.charAt(0).toUpperCase() + part.slice(1)
            : part.toUpperCase(),
        )
        .join(" ")
    : "—";

const STATUS_STYLES = {
  requested: { label: "Requested", chip: "bg-amber-100 text-amber-800" },
  sent_out: { label: "Sent out", chip: "bg-blue-100 text-blue-800" },
  received: { label: "Received", chip: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelled", chip: "bg-gray-100 text-gray-600" },
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "requested", label: "Requested" },
  { value: "sent_out", label: "Sent out" },
  { value: "received", label: "Received" },
];

export default function JerseySizeRequests() {
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await jerseyService.getSizeRequests(statusFilter || undefined);
      setItems(data?.items || []);
      setOpenCount(data?.open_count || 0);
    } catch (error) {
      console.error("Failed to load size requests:", error);
      toast.error("Could not load size requests. Please try again.");
      setItems([]);
      setOpenCount(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((row) =>
      [row.child_name, row.class_name, row.school_name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [items, searchQuery]);

  const runAction = async (id, action, message) => {
    if (busyId) return;
    try {
      setBusyId(id);
      await action(id);
      toast.success(message);
      fetchRequests();
    } catch (error) {
      console.error("Size request action failed:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not update that request. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: "child_name",
      label: "Player",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-semibold text-[#173151]">{value || "N/A"}</p>
          <p className="text-xs text-gray-500">
            {[row.class_name, row.school_name].filter(Boolean).join(" · ")}
          </p>
        </div>
      ),
    },
    {
      key: "original_size",
      label: "Original",
      render: (value) => (
        <span className="text-gray-600">{formatSize(value)}</span>
      ),
    },
    {
      key: "requested_size",
      label: "Requested",
      render: (value) => (
        <span className="font-semibold text-[#173151]">
          {formatSize(value)}
        </span>
      ),
    },
    {
      key: "requested_at",
      label: "Requested on",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="text-gray-700">{formatDate(value)}</p>
          <p className="text-xs text-gray-500">
            {row.requested_by_name ? `by ${row.requested_by_name}` : ""}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (value, row) => {
        const meta = STATUS_STYLES[value] || STATUS_STYLES.requested;
        return (
          <div>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${meta.chip}`}
            >
              {meta.label}
            </span>
            {value === "sent_out" && row.sent_out_at && (
              <p className="text-xs text-gray-500 mt-1">
                Sent {formatDate(row.sent_out_at)}
              </p>
            )}
            {value === "received" && row.received_at && (
              <p className="text-xs text-gray-500 mt-1">
                Received {formatDate(row.received_at)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Action",
      render: (_value, row) => {
        if (row.status === "requested") {
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  runAction(
                    row.id,
                    jerseyService.markSizeRequestSent,
                    `Marked sent for ${row.child_name}.`,
                  )
                }
                disabled={busyId === row.id}
                className="inline-flex items-center gap-1.5 bg-[#173151] text-white text-xs font-semibold rounded-lg px-3 py-1.5 hover:bg-[#1f3d67] disabled:opacity-50 whitespace-nowrap"
              >
                <Send className="w-3 h-3" />
                Mark sent
              </button>
              <button
                type="button"
                onClick={() =>
                  runAction(
                    row.id,
                    jerseyService.cancelSizeRequest,
                    "Request cancelled.",
                  )
                }
                disabled={busyId === row.id}
                aria-label={`Cancel request for ${row.child_name}`}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        }
        if (row.status === "sent_out") {
          return (
            <span className="text-xs text-gray-500">
              Waiting on the coach to confirm
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 font-semibold">
            {row.status === "received" && <Check className="w-3 h-3" />}
            {STATUS_STYLES[row.status]?.label || ""}
          </span>
        );
      },
    },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-9xl mx-auto px-3 sm:px-4 py-4 flex-1 flex flex-col min-h-0 w-full">
        <div className="shrink-0 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center items-start gap-3 sm:gap-4 justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif truncate">
              New Size Requests
            </h1>
            <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1 hidden sm:block">
              Replacement jerseys, from the coach&rsquo;s request to the player
              receiving it
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 min-w-[80px] sm:min-w-[140px] shrink-0">
            <p className="text-xs sm:text-sm text-gray-600 font-manrope">
              Still open
            </p>
            <p className="text-lg sm:text-2xl font-bold text-[#173151] font-manrope mt-1">
              {openCount}
            </p>
          </div>
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-2 mb-4">
          <Ruler className="w-4 h-4 text-gray-400" />
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-manrope font-medium border transition-colors ${
                statusFilter === option.value
                  ? "bg-[#173151] text-white border-[#173151]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#173151]"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <FilterBar
          onSearch={setSearchQuery}
          searchValue={searchQuery}
          searchPlaceholder="Player, class or school..."
          onClearFilters={() => setSearchQuery("")}
          hasActiveFilters={Boolean(searchQuery)}
        />

        <div className="flex-1 min-h-0 overflow-auto">
          <DataTable
            columns={columns}
            data={filtered}
            loading={loading}
            totalItems={filtered.length}
            emptyMessage="No size requests to work through."
          />
        </div>
      </div>
    </div>
  );
}
